import React from "react";
import { motion } from "framer-motion";
import {
  Phone,
  ShieldCheck,
  FileText,
  Wallet,
  PhoneCall,
  CalendarCheck,
  MessageSquare,
  Stethoscope,
  Receipt,
  FileSearch,
  Brain,
  BarChart3,
  FileBox,
  FilePlus2,
  UserPlus,
  Send,
  Sliders,
  Power,
} from "lucide-react";

const agents = [
  { name: "Receptionist",     icon: Phone,         on: true,  load: 0.78 },
  { name: "Insurance Verif.", icon: ShieldCheck,   on: true,  load: 0.62 },
  { name: "Claim Submission", icon: Send,          on: true,  load: 0.34 },
  { name: "Claim Scrubbing",  icon: FileSearch,    on: true,  load: 0.48 },
  { name: "Denials",          icon: FileText,      on: true,  load: 0.55 },
  { name: "EOB Posting",      icon: Receipt,       on: true,  load: 0.28 },
  { name: "Insurance Calls",  icon: PhoneCall,     on: true,  load: 0.88 },
  { name: "AR & Collections", icon: Wallet,        on: true,  load: 0.40 },
  { name: "Hygiene Recall",   icon: CalendarCheck, on: true,  load: 0.52 },
  { name: "Patient Intake",   icon: UserPlus,      on: true,  load: 0.36 },
  { name: "Case Acceptance",  icon: FilePlus2,     on: false, load: 0.0  },
  { name: "Pt. Experience",   icon: MessageSquare, on: true,  load: 0.22 },
  { name: "Fax Manager",      icon: FileBox,       on: true,  load: 0.18 },
  { name: "Scribe",           icon: Stethoscope,   on: false, load: 0.0  },
  { name: "Practice Manager", icon: Brain,         on: true,  load: 0.44 },
  { name: "Analytics",        icon: BarChart3,     on: true,  load: 0.30 },
];

const Toggle = ({ on, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ delay, duration: 0.4 }}
    className={`relative h-3.5 w-6 rounded-full ${
      on ? "bg-gradient-to-r from-blue-500 to-indigo-500" : "bg-slate-200"
    }`}
    style={
      on
        ? { boxShadow: "0 0 12px rgba(99,102,241,0.45)" }
        : {}
    }
  >
    <motion.div
      className="absolute top-0.5 h-2.5 w-2.5 rounded-full bg-white"
      animate={{ left: on ? "12px" : "2px" }}
      transition={{ type: "spring", stiffness: 320, damping: 24 }}
      style={{ boxShadow: "0 1px 3px rgba(15,23,42,0.18)" }}
    />
  </motion.div>
);

const AgentTile = ({ agent, i }) => {
  const Icon = agent.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: 0.04 * i }}
      className={`relative rounded-xl border bg-white px-3 py-2.5 overflow-hidden ${
        agent.on
          ? "border-slate-200 shadow-[0_4px_20px_-12px_rgba(59,130,246,0.4)]"
          : "border-slate-200/70"
      }`}
    >
      {/* Top row: icon + name + toggle */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div
            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${
              agent.on
                ? "bg-gradient-to-br from-blue-500 to-indigo-600 text-white"
                : "bg-slate-100 text-slate-400"
            }`}
          >
            <Icon className="h-3 w-3" />
          </div>
          <span
            className={`text-[11px] font-semibold truncate ${
              agent.on ? "text-slate-900" : "text-slate-400"
            }`}
          >
            {agent.name}
          </span>
        </div>
        <Toggle on={agent.on} delay={0.2 + 0.04 * i} />
      </div>

      {/* Animated bar */}
      <div className="mt-2 h-1 w-full rounded-full bg-slate-100 overflow-hidden">
        {agent.on ? (
          <motion.div
            className="h-full rounded-full bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500"
            initial={{ width: "0%" }}
            animate={{
              width: [
                `${agent.load * 100}%`,
                `${Math.min(100, agent.load * 100 + 6)}%`,
                `${agent.load * 100}%`,
              ],
            }}
            transition={{
              width: {
                duration: 3.5 + (i % 5) * 0.4,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 0.3 + 0.04 * i,
              },
            }}
          />
        ) : (
          <div className="h-full w-0" />
        )}
      </div>

      {/* Cap pill — abstract, no number */}
      <div className="mt-1.5 flex items-center justify-between">
        <div className="flex items-center gap-1">
          <Sliders className="h-2.5 w-2.5 text-slate-400" />
          <span className="text-[9px] font-mono uppercase tracking-[0.12em] text-slate-400">
            cap set
          </span>
        </div>
        {agent.on ? (
          <motion.div
            className="h-1.5 w-1.5 rounded-full bg-emerald-500"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          />
        ) : (
          <span className="text-[9px] font-mono uppercase tracking-[0.12em] text-slate-300">
            paused
          </span>
        )}
      </div>
    </motion.div>
  );
};

// Big animated spend gauge (no real number)
const SpendGauge = () => {
  const size = 200;
  const stroke = 14;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      {/* outer glow */}
      <motion.div
        aria-hidden
        className="absolute inset-0 rounded-full"
        style={{
          background:
            "radial-gradient(circle, rgba(99,102,241,0.35) 0%, rgba(99,102,241,0) 70%)",
          filter: "blur(20px)",
        }}
        animate={{ opacity: [0.5, 0.85, 0.5], scale: [0.95, 1.05, 0.95] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      />

      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="relative -rotate-90"
      >
        <defs>
          <linearGradient id="gaugeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="50%" stopColor="#6366f1" />
            <stop offset="100%" stopColor="#8b5cf6" />
          </linearGradient>
        </defs>
        {/* track */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke="rgba(148,163,184,0.18)"
          strokeWidth={stroke}
          fill="none"
        />
        {/* progress arc — animates in then breathes */}
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke="url(#gaugeGrad)"
          strokeWidth={stroke}
          strokeLinecap="round"
          fill="none"
          strokeDasharray={c}
          initial={{ strokeDashoffset: c }}
          animate={{
            strokeDashoffset: [c * 0.42, c * 0.36, c * 0.42],
          }}
          transition={{
            duration: 5.5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0.6,
          }}
        />
        {/* cap marker tick */}
        <motion.circle
          cx={size / 2}
          cy={stroke / 2}
          r={4}
          fill="#fbbf24"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0.6, 1, 0.6] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          style={{
            filter: "drop-shadow(0 0 6px rgba(251,191,36,0.7))",
            transformOrigin: `${size / 2}px ${size / 2}px`,
            transform: "rotate(216deg)",
          }}
        />
      </svg>

      {/* center label */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <p className="text-[9.5px] font-bold uppercase tracking-[0.18em] text-slate-400">
          Monthly AI spend
        </p>
        <div className="mt-1.5 flex items-end gap-1">
          {[0, 1, 2, 3].map((b) => (
            <motion.div
              key={b}
              className="w-1.5 rounded-sm bg-gradient-to-t from-blue-500 to-indigo-500"
              animate={{ height: [10, 22, 14, 26, 12] }}
              transition={{
                duration: 2.2 + b * 0.3,
                repeat: Infinity,
                ease: "easeInOut",
                delay: b * 0.2,
              }}
            />
          ))}
        </div>
        <p className="mt-2 text-[10px] font-mono uppercase tracking-[0.16em] text-slate-500">
          well under cap
        </p>
      </div>
    </div>
  );
};

const SpaCreekAIDashboardSlide = () => {
  return (
    <div className="h-full w-full bg-[#f8fafc] flex items-center justify-center p-6 xl:p-10 relative overflow-hidden font-sans">
      {/* Ambient gradient blurs */}
      <div
        className="absolute top-[-200px] right-[-100px] w-[700px] h-[700px] rounded-full blur-[120px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #93c5fd 0%, #c4b5fd 50%, #fbcfe8 100%)" }}
      />
      <div
        className="absolute bottom-[-200px] left-[-100px] w-[600px] h-[600px] rounded-full blur-[120px] opacity-20 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #a5f3fc 0%, #93c5fd 50%, #c4b5fd 100%)" }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_0.8px,transparent_0.8px)] [background-size:28px_28px] opacity-40 pointer-events-none" />

      <div className="w-full max-w-[1200px] z-10 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-7"
        >
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-2">
            AI utilization management
          </p>
          <h2 className="text-3xl lg:text-[44px] font-bold text-slate-900 tracking-tight leading-tight">
            You decide{" "}
            <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 bg-clip-text text-transparent">
              how much AI you spend.
            </span>
          </h2>
        </motion.div>

        {/* Main two-column visual */}
        <div className="grid grid-cols-[280px_1fr] gap-10 items-center">
          {/* Left: gauge + control labels */}
          <div className="flex flex-col items-center gap-5">
            <SpendGauge />
            <div className="flex flex-col items-center gap-2.5 text-center">
              <div className="flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50/60 px-3 py-1">
                <Power className="h-3 w-3 text-blue-600" />
                <span className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-blue-700">
                  Toggle agents
                </span>
              </div>
              <div className="flex items-center gap-2 rounded-full border border-indigo-200 bg-indigo-50/60 px-3 py-1">
                <Sliders className="h-3 w-3 text-indigo-600" />
                <span className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-indigo-700">
                  Set credit caps
                </span>
              </div>
              <div className="flex items-center gap-2 rounded-full border border-violet-200 bg-violet-50/60 px-3 py-1">
                <motion.span
                  className="h-2 w-2 rounded-full bg-violet-500"
                  animate={{ opacity: [0.4, 1, 0.4] }}
                  transition={{ duration: 1.8, repeat: Infinity }}
                />
                <span className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-violet-700">
                  Hard $ ceiling
                </span>
              </div>
            </div>
          </div>

          {/* Right: animated grid of agent tiles */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="relative"
          >
            {/* glow behind grid */}
            <div
              aria-hidden
              className="absolute -inset-6 rounded-3xl pointer-events-none"
              style={{
                background:
                  "radial-gradient(ellipse at center, rgba(99,102,241,0.10) 0%, rgba(99,102,241,0) 70%)",
              }}
            />
            <div className="relative grid grid-cols-4 gap-2.5">
              {agents.map((a, i) => (
                <AgentTile key={a.name} agent={a} i={i} />
              ))}
            </div>
          </motion.div>
        </div>

        {/* Bottom caption */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="text-center text-[13px] text-slate-600 mt-7 max-w-[820px] mx-auto leading-relaxed"
        >
          Every agent has its own switch, its own credit budget, and rolls up
          to a hard monthly dollar ceiling.{" "}
          <span className="font-semibold text-slate-900">
            Spend is in your hands — not ours.
          </span>
        </motion.p>
      </div>
    </div>
  );
};

export default SpaCreekAIDashboardSlide;
